import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Mic, Sparkles, Plus, RefreshCcw, X } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

// Persist a session id per browser
const getSessionId = () => {
    let s = localStorage.getItem("ai_session_id");
    if (!s) {
        s = `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        localStorage.setItem("ai_session_id", s);
    }
    return s;
};

const SAMPLE_PROMPTS_EL = [
    "Πεινάω σαν λύκος",
    "Κάτι ελαφρύ αλλά ζουμερό",
    "Πιο spicy επιλογή;",
    "Surprise me",
];
const SAMPLE_PROMPTS_EN = [
    "I want something heavy",
    "Cheesy and dirty",
    "Got anything spicy?",
    "Surprise me",
];

export const AIHome = () => {
    const { add } = useCart();
    const [messages, setMessages] = useState([
        {
            role: "ai",
            text: "I'm GRILL. Tell me what you're craving — or stop wasting both our time.",
            recs: [],
        },
    ]);
    const [input, setInput] = useState("");
    const [thinking, setThinking] = useState(false);
    const [recording, setRecording] = useState(false);
    const [lang, setLang] = useState("en");
    const threadRef = useRef(null);

    useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [messages, thinking]);

    const detectLang = (text) => /[\u0370-\u03ff\u1f00-\u1fff]/.test(text) ? "el" : "en";

    const sendMessage = async (text, surprise = false) => {
        const trimmed = text.trim();
        if (!trimmed || thinking) return;
        const detected = detectLang(trimmed);
        setLang(detected);

        setMessages((m) => [...m, { role: "user", text: trimmed }]);
        setInput("");
        setThinking(true);

        try {
            const endpoint = surprise ? "/ai/surprise" : "/ai/chat";
            const { data } = await api.post(endpoint, {
                session_id: getSessionId(),
                message: trimmed,
            });
            setMessages((m) => [
                ...m,
                { role: "ai", text: data.reply, recs: data.recommendations || [] },
            ]);
        } catch (err) {
            setMessages((m) => [
                ...m,
                {
                    role: "ai",
                    text: detected === "el"
                        ? "Κάτι έσπασε στο σύστημα. Δοκίμασε ξανά."
                        : "System glitch. Try again.",
                    recs: [],
                },
            ]);
        } finally {
            setThinking(false);
        }
    };

    const handleAdd = (item) => {
        add(item);
        const sass = lang === "el"
            ? ["Καλό. Στο καλάθι.", "Έγινε. Σωστή κίνηση.", "Πάει αυτό."]
            : ["Locked in.", "Solid. Added.", "Smart pick."];
        toast.success(sass[Math.floor(Math.random() * sass.length)]);
    };

    const handleSurprise = () => {
        sendMessage(lang === "el" ? "Surprise me" : "Surprise me", true);
    };

    // Simulated voice recording (no actual audio capture)
    const toggleVoice = () => {
        if (recording) {
            setRecording(false);
            const fake = lang === "el" ? "Πεινάω, βάλε μου κάτι δυνατό" : "I'm hungry, hit me with something heavy";
            sendMessage(fake);
            return;
        }
        setRecording(true);
        toast(lang === "el" ? "🎤 Άκούω..." : "🎤 Listening...", { duration: 1800 });
        setTimeout(() => {
            // Auto-stop simulation after 2.5s
            setRecording((r) => {
                if (r) {
                    const fake = lang === "el" ? "Πεινάω, βάλε μου κάτι δυνατό" : "I'm hungry, hit me with something heavy";
                    sendMessage(fake);
                    return false;
                }
                return r;
            });
        }, 2500);
    };

    const samples = lang === "el" ? SAMPLE_PROMPTS_EL : SAMPLE_PROMPTS_EN;

    return (
        <div data-testid="ai-home" className="cyber-bg cyber-grid scanlines relative min-h-screen flex flex-col text-white overflow-hidden">
            {/* Top bar */}
            <header className="relative z-20 flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full bg-[var(--neon-green)] shadow-[0_0_12px_var(--neon-green)] animate-pulse" />
                    <span className="text-[11px] font-mono tracking-[0.3em] text-white/70 uppercase">GRILL · ONLINE</span>
                </div>
                <Link
                    to="/menu"
                    data-testid="view-menu-link"
                    className="text-[11px] font-mono tracking-widest text-white/60 hover:text-[var(--neon-green)] uppercase transition border-b border-white/15 hover:border-[var(--neon-green)] pb-0.5"
                >
                    {lang === "el" ? "Δες όλο το μενού" : "View full menu"} →
                </Link>
            </header>

            {/* Orb */}
            <div className="relative z-10 flex justify-center pt-3 pb-6">
                <div className={thinking ? "orb orb-thinking" : "orb"} aria-label="GRILL AI orb" />
            </div>

            {/* Conversation thread */}
            <div
                ref={threadRef}
                data-testid="ai-thread"
                className="relative z-10 flex-1 overflow-y-auto px-4 sm:px-6 pb-4 space-y-4 scrollbar-hide"
            >
                <div className="mx-auto max-w-2xl space-y-4">
                    {messages.map((m, i) => (
                        <div key={i} className={`msg-rise flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            {m.role === "ai" ? (
                                <div className="max-w-[85%] space-y-3">
                                    <div className="glass-neon px-4 py-3 rounded-2xl rounded-tl-sm">
                                        <p className="text-sm sm:text-base leading-relaxed text-white">{m.text}</p>
                                    </div>
                                    {m.recs && m.recs.length > 0 && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                            {m.recs.map((rec, ri) => (
                                                <RecCard key={`${i}-${ri}`} item={rec} onAdd={handleAdd} onSwap={() => sendMessage(lang === "el" ? "Δείξε μου κάτι άλλο" : "Show me something else")} delay={ri * 120} lang={lang} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="max-w-[80%] bg-white/5 border border-white/10 px-4 py-2.5 rounded-2xl rounded-tr-sm">
                                    <p className="text-sm sm:text-base text-white/90">{m.text}</p>
                                </div>
                            )}
                        </div>
                    ))}

                    {thinking && (
                        <div className="flex justify-start msg-rise">
                            <div className="glass-neon px-4 py-3 rounded-2xl rounded-tl-sm flex items-center gap-1.5">
                                <span className="dot" />
                                <span className="dot" />
                                <span className="dot" />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sample prompts */}
            {messages.length <= 1 && (
                <div className="relative z-10 px-4 sm:px-6 pb-2">
                    <div className="mx-auto max-w-2xl flex flex-wrap gap-2 justify-center">
                        {samples.map((s) => (
                            <button
                                key={s}
                                data-testid={`sample-${s}`}
                                onClick={() => s.toLowerCase().includes("surprise") ? handleSurprise() : sendMessage(s)}
                                className="text-xs px-3 h-8 rounded-full border border-white/15 bg-white/5 hover:border-[var(--neon-green)] hover:text-[var(--neon-green)] hover:bg-[var(--neon-green)]/5 transition text-white/70"
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Input bar */}
            <div className="relative z-20 px-4 sm:px-6 pb-5 pt-3">
                <div className="mx-auto max-w-2xl glass rounded-full p-1.5 flex items-center gap-1.5 border border-[var(--neon-green)]/30 shadow-[0_0_30px_rgba(57,255,143,0.15)]">
                    <button
                        data-testid="ai-mic-btn"
                        onClick={toggleVoice}
                        aria-label="voice input"
                        className={`shrink-0 h-11 w-11 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 transition ${recording ? "mic-recording" : ""}`}
                    >
                        <Mic size={18} />
                    </button>
                    <input
                        data-testid="ai-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
                        placeholder={lang === "el" ? "Πες μου τι θες..." : "Tell me what you're craving..."}
                        className="flex-1 bg-transparent outline-none text-white placeholder:text-white/40 px-2 text-base"
                    />
                    <button
                        data-testid="ai-surprise-btn"
                        onClick={handleSurprise}
                        title={lang === "el" ? "Έκπληξε με" : "Surprise me"}
                        className="shrink-0 hidden sm:grid h-11 w-11 place-items-center rounded-full bg-white/5 hover:bg-[var(--neon-red)]/20 hover:text-[var(--neon-red)] text-white/70 transition"
                    >
                        <Sparkles size={18} />
                    </button>
                    <button
                        data-testid="ai-send-btn"
                        onClick={() => sendMessage(input)}
                        disabled={!input.trim() || thinking}
                        className="shrink-0 h-11 w-11 grid place-items-center rounded-full bg-[var(--neon-green)] text-black hover:shadow-[0_0_24px_rgba(57,255,143,0.7)] disabled:opacity-40 disabled:pointer-events-none transition active:scale-90"
                    >
                        <Send size={18} strokeWidth={2.5} />
                    </button>
                </div>
                <p className="text-center text-[10px] font-mono tracking-widest uppercase text-white/30 mt-3">
                    {lang === "el" ? "Powered by GRILL AI · Edgy by design" : "Powered by GRILL AI · Edgy by design"}
                </p>
            </div>
        </div>
    );
};

const RecCard = ({ item, onAdd, onSwap, delay, lang }) => {
    return (
        <article
            data-testid={`rec-card-${item.id}`}
            className="card-slide relative group rounded-2xl overflow-hidden glass border border-[var(--neon-red)]/30 hover:border-[var(--neon-red)] transition shadow-[0_0_30px_rgba(255,45,79,0.12)] hover:shadow-[0_0_40px_rgba(255,45,79,0.35)]"
            style={{ animationDelay: `${delay}ms` }}
        >
            <div className="relative aspect-[16/10] overflow-hidden bg-black">
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/70 text-[var(--neon-green)] font-mono text-xs font-bold border border-[var(--neon-green)]/40 shadow-[0_0_12px_rgba(57,255,143,0.4)]">
                    €{item.price.toFixed(2)}
                </span>
                <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="font-heading uppercase text-xl text-white drop-shadow-lg">{item.name}</h3>
                </div>
            </div>
            <div className="p-3">
                <p className="text-xs text-white/60 line-clamp-2 mb-3 min-h-[32px]">{item.description}</p>
                <div className="flex gap-2">
                    <button
                        data-testid={`rec-add-${item.id}`}
                        onClick={() => onAdd(item)}
                        className="neon-btn flex-1 h-10 rounded-full font-mono uppercase tracking-widest text-xs font-bold flex items-center justify-center gap-1.5"
                    >
                        <Plus size={14} strokeWidth={3} /> {lang === "el" ? "Στο καλάθι" : "Add to order"}
                    </button>
                    <button
                        data-testid={`rec-swap-${item.id}`}
                        onClick={onSwap}
                        title={lang === "el" ? "Δείξε άλλο" : "Show me something else"}
                        className="h-10 w-10 grid place-items-center rounded-full bg-white/5 border border-white/15 hover:border-[var(--neon-red)] hover:text-[var(--neon-red)] transition"
                    >
                        <RefreshCcw size={14} />
                    </button>
                </div>
            </div>
        </article>
    );
};
